import React, { Context, JSX, ReactNode, createContext, useContext, useEffect, useState } from 'react';

import qs from 'qs';
import { useLocation } from 'react-router-dom';

import { CruisesContract as Api } from '@luxuryescapes/contract-svc-cruise';

import useAppNavigation from '~/hooks/useAppNavigation';

import shipService from '~/services/cruises/ShipService';

import { parseSearchString } from '~/utils/url';

export interface Filter {
  name?: string;
  cruiseLine?: string;
}

interface CruisesData {
  ships: Array<Api.Ship>;
  page: number;
  total: number;
  shipsPerPage: number;
  paginate: (page: number) => void;
  setFilter: (value: Filter) => void;
  filter: Filter;
  isLoading: boolean;
}

interface CruiseShipProviderProps {
  children?: ReactNode;
}

export const CruiseShipContext: Context<CruisesData> = createContext({} as CruisesData);

const shipsPerPage = 15;

export function CruiseShipProvider({ children }: CruiseShipProviderProps): JSX.Element {
  const { search } = useLocation();
  const queryString = qs.parse(search.slice(1));

  const [filter, setFilter] = useState<Filter>(queryString);
  const { push: setQueryString } = useAppNavigation();
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [ships, setShips] = useState<Array<Api.Ship>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getShipsList = async () => {
    setIsLoading(true);
    const skip = (page - 1) * shipsPerPage;
    const res = await shipService.listWithPagination({
      ...(filter.name && { name: filter.name }),
      ...(filter.cruiseLine && { cruiseLine: filter.cruiseLine }),
      skip,
      take: shipsPerPage,
    });
    setShips(res.result);
    setTotal(res.total);
    setIsLoading(false);
  };

  const setCurrentPage = (): void => {
    const queryString = parseSearchString(search);
    if (queryString) {
      const pageToGo = parseInt(queryString.page as string);
      if (pageToGo) {
        setPage(pageToGo);
      }
    }
  };

  const paginate = (page: number): void => {
    const queryString = new URLSearchParams({
      page: page.toString(),
    }).toString();
    setQueryString({ search: queryString });
    setPage(page);
  };

  useEffect(() => {
    setCurrentPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getShipsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter]);

  return (
    <CruiseShipContext.Provider
      value={{
        filter,
        setFilter,
        ships,
        page,
        total,
        shipsPerPage,
        paginate,
        isLoading,
      }}
    >
      {children}
    </CruiseShipContext.Provider>
  );
}

export const useCruiseShips = (): CruisesData => useContext(CruiseShipContext);
